import { first,  takeUntil, filter, flatMap, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Component, OnDestroy } from '@angular/core';
import { UserService } from '../services/user.service';
import { ActivatedRoute } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'users',
    styles: [`
        .scroll-container {
            max-height: calc(100vh - 132px);
            overflow-y: auto;
        }

        td.mat-cell.cdk-column-actions.mat-column-actions {
            width: 65px;
        }
    `],
    template: `
        <div fxLayout="column">
        <mat-toolbar>
            <h3>Members</h3>
            <span fxFlex></span>
            <mat-form-field style="padding-right: 50px; font-size: 16px; font-weight: 400;" class="app-input-white-underline">
                <input matInput [formControl]="searchBox" placeholder="Search Members">
            </mat-form-field>
            <button mat-icon-button [routerLink]="['./create']">
                <mat-icon aria-label="Invite Users">person_add</mat-icon>
            </button>
        </mat-toolbar>
        <div *ngIf="noTouching" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 25px;">
            <spinner></spinner>
        </div>
        <div fxLayout="column"
             infiniteScroll 
             [infiniteScrollThrottle]="300" 
             [infiniteScrollDistance]="1" 
             [scrollWindow]="false" 
             (scrolled)="nextPage()" 
             class="scroll-container">
            <table *ngIf="!noTouching" 
                   mat-table
                   matSort 
                   (matSortChange)="sort($event)"
                   [matSortDisableClear]="true "
                   [matSortActive]="searchParams?.value?.sort" 
                   [matSortDirection]="searchParams?.value?.sortDirection" 
                   [dataSource]="users" 
                   fxFlex="100" 
                   style="width: 100%;">
                <ng-container matColumnDef="name" >
                    <th mat-header-cell 
                        mat-sort-header="lastNameLower"
                        *matHeaderCellDef>Name</th>
                    <td mat-cell *matCellDef="let row" >{{row.firstName}} {{row.lastName}}</td>
                </ng-container>
                <ng-container matColumnDef="email">
                    <th mat-header-cell
                        mat-sort-header="emailAddress" 
                        *matHeaderCellDef>Email</th>
                    <td mat-cell *matCellDef="let row" >{{row.emailAddress}}</td>
                </ng-container>
                <ng-container matColumnDef="type">
                    <th mat-header-cell 
                        *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let row" >{{userType(row)}}</td>
                </ng-container>

                <ng-container matColumnDef="created">
                    <th mat-header-cell
                        mat-sort-header="created" 
                        *matHeaderCellDef>Date Added</th>
                    <td mat-cell *matCellDef="let row" >{{row.created | date:'shortDate'}}</td>
                </ng-container>

                <ng-container matColumnDef="lastActivityDate">
                    <th mat-header-cell
                        mat-sort-header="lastActivityDate" 
                        *matHeaderCellDef>Last Active Date</th>
                    <td mat-cell *matCellDef="let row" >{{row.lastActivityDate | date:'shortDate'}}</td>
                </ng-container>

                <ng-container matColumnDef="status">
                    <th mat-header-cell
                        mat-sort-header="status" 
                        *matHeaderCellDef>Status</th>
                    <td mat-cell *matCellDef="let row" >{{row.status}}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let row">
                        <mat-menu #menu="matMenu">
                            <button mat-menu-item [routerLink]="[row.id]">
                                <mat-icon>build</mat-icon>
                                <span>Edit</span>
                            </button>
                            <button mat-menu-item (click)="deleteUser(row)" [disabled]="row.id === currentUser.id">
                                <mat-icon>clear</mat-icon>
                                <span>Delete</span>
                            </button>
                        </mat-menu>

                        <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                            <mat-icon>settings</mat-icon>
                        </button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="columns"></tr>
                <tr mat-row *matRowDef="let row; columns: columns;"></tr>
            </table>
            <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                <spinner></spinner>
            </div>
        </div>
        </div>
    `
})
export class UserListComponent implements OnDestroy {
    private cleanupTime = new Subject<void>();
    currentUser: any;
    users = new BehaviorSubject<any[]>([]);
    searchParams = new BehaviorSubject<any>({ page: 0 });
    lastFetched: boolean;
    noTouching: boolean;
    searchBox = new FormControl();
    columns = ['name', 'email', 'type', 'created', 'lastActivityDate', 'status', 'actions'];
    noTouchingPaging: boolean;
    
    constructor(route: ActivatedRoute, 
                private dialog: MatDialog,
                public snackBar: MatSnackBar,
                private permissionService: PermissionService, 
                private userService: UserService) {
        this.currentUser = this.userService.getCurrentUser();
        route.queryParams.pipe(takeUntil(this.cleanupTime)).subscribe(queryParams => {
            this.noTouching = true;
            this.users.next([]);
            this.searchParams.next(Object.assign({}, this.searchParams.value, { 
                sort: queryParams.sort || this.searchParams.value.sort,
                sortDirection: queryParams.sortDirection || 'asc'
             }));
        });
        this.searchBox.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.cleanupTime))
        .subscribe(searchTerm => {
            this.users.next([]);
            this.searchParams.next(Object.assign({}, this.searchParams.value, { searchTerm, page: 0 }));
        });
        this.searchParams.pipe(
            flatMap(params => this.userService.searchUsers(params)), 
            tap(() => this.noTouching = false),
            tap(() => this.noTouchingPaging = false),
            tap(results => this.lastFetched = results.last),
            takeUntil(this.cleanupTime)
        ).subscribe(results => this.users.next(this.users.value.concat(results.content)));
    }

    userType(user) {
        if(this.permissionService.userHasTenantAction(user, 'MANAGE_TENANT')) {
            return 'Admin';
        }
        if(this.permissionService.userHasTenantAction(user, 'CREATE_STREAM')) {
            return 'Topic Creator';
        }
        return 'Read Only';
    }

    nextPage() {
         if (!this.lastFetched) {
            this.searchParams.next(Object.assign({}, this.searchParams.value, { page: ++this.searchParams.value.page }));
         }
    }

    sort(sort: Sort): void {
        const params = this.searchParams.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.noTouchingPaging = true;
        this.users.next([]);
        this.searchParams.next(params);
    }

    deleteUser(user) {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete ${user.firstName} ${user.lastName}?`,
                content: `
                    Warning! This will <b>permanently</b> remove this user from the system.
                    <br>By clicking the Delete button below, you are confirming that you are removing this user. 
                `, 
                actionLabel: 'Delete'}
        }).afterClosed().pipe(first()).pipe(
            filter(doIt => doIt),
            tap(() => this.noTouching = true),
            flatMap(() => this.userService.deleteUser(user.id).pipe(first()))
        ).subscribe(
            () => {
                this.snackBar.open(`${user.firstName} ${user.lastName} has been deleted.`, 'OK', { duration: 3000});
                this.users.next([]);
                this.searchParams.next(this.searchParams.value);
            },
            () => {
                this.noTouching = false;
                this.snackBar.open(`The user ${user.firstName} ${user.lastName} couldn't be deleted because they own streams or groups.`, 'OK', { duration: 3000});
            }
        );
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}